import type { MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';
import type { AcademyTrainingsListingResponse } from '~/types/academy/academy';
import type { RelatedTrainingsMode } from '~/types/academy/courseRelations';
import { withResolvedCtaUrl, withTeaserColor } from '~/utils/academy/trainings/enrichTrainingsListingItem';
import { ACADEMY_AGGREGATED_REQUEST_TIMEOUT, ACADEMY_TRAININGS_RELATED_KEY_PREFIX } from '~/utils/academy/constants';

export function useAcademyRelatedTrainings(
    courseId: MaybeRefOrGetter<string>,
    mode: MaybeRefOrGetter<RelatedTrainingsMode> = 'same_catalog',
    relatedCourseIds: MaybeRefOrGetter<number[]> = []
) {
    const localizedPath = useLocalizedPath();
    const academyPath = useRequiredSectionPathById('academy');
    const { urlSegment } = useLanguage();
    const auth = useAuth();
    const isAuthenticated = computed(() => Boolean(auth.value));
    const hasValidCourseId = computed(() => {
        const id = Number(toValue(courseId));
        return Number.isSafeInteger(id) && id > 0;
    });
    const canFetch = computed(() => isAuthenticated.value && hasValidCourseId.value);

    const query = computed(() => {
        const ids = toValue(relatedCourseIds);

        return {
            mode: toValue(mode),
            ...(ids.length > 0 ? { course_ids: ids.join(',') } : {})
        };
    });

    // Watch the serialized request, not query object identities replaced by bridge updates.
    const { data, error, status } = useAsyncData(
        () =>
            `${ACADEMY_TRAININGS_RELATED_KEY_PREFIX}:${toValue(courseId)}:${JSON.stringify(query.value)}:${auth.value?.sub ?? 'anonymous'}`,
        (_nuxtApp, { signal }) => {
            return $fetch<AcademyTrainingsListingResponse>(`/api/academy/trainings/${toValue(courseId)}/related`, {
                credentials: 'include',
                query: query.value,
                signal
            });
        },
        {
            server: false,
            enabled: canFetch,
            timeout: ACADEMY_AGGREGATED_REQUEST_TIMEOUT
        }
    );

    const items = computed(() =>
        (canFetch.value ? (data.value?.items ?? []) : [])
            .map(withTeaserColor)
            .map((item) => withResolvedCtaUrl(item, localizedPath, urlSegment.value, academyPath.value))
    );
    const hasItems = computed(() => items.value.length > 0);
    const prettyError = computed(() => (error.value ? JSON.stringify(error.value, null, 2) : null));
    // Client-only requests are idle during server rendering, before the browser starts the request.
    const isInitialLoading = computed(
        () => canFetch.value && (status.value === 'idle' || status.value === 'pending') && items.value.length === 0
    );

    return {
        isAuthenticated,
        hasValidCourseId,
        data,
        error,
        prettyError,
        isInitialLoading,
        status,
        items,
        hasItems
    };
}
